import { create } from "zustand";
import { superChatContentApi } from "../apis/superChatContent";
import {
  SuperChatContentDto,
  CreateSuperChatContentDto,
  UpdateSuperChatContentDto,
  SuperChatContentListDto,
} from "../apis/types/SuperChatContentTypes";

interface ContentFilters {
  category: string;
  search: string;
  useYn: "" | "Y" | "N";
}

interface PaginationState {
  page: number;
  limit: number;
  pageSize: number;
  total: number;
  size: number;
}

interface SuperChatContentState {
  // 데이터
  contents: SuperChatContentDto[];
  currentContent: SuperChatContentDto | null;

  // UI 상태
  loading: boolean;
  error: string | null;

  // 필터링 및 페이지네이션
  filters: ContentFilters;
  pagination: PaginationState;

  // 선택된 항목들 (벌크 작업용)
  selectedIds: number[];
}

interface SuperChatContentActions {
  // 컨텐츠 CRUD
  fetchContents: (params?: any) => Promise<void>;
  fetchContentDetail: (id: number) => Promise<void>;
  createContent: (
    data: CreateSuperChatContentDto
  ) => Promise<SuperChatContentDto>;
  updateContent: (
    id: number,
    data: UpdateSuperChatContentDto
  ) => Promise<SuperChatContentDto>;
  deleteContent: (id: number) => Promise<void>;

  // 벌크 작업
  bulkUpdateContents: (action: string, ids: number[]) => Promise<void>;

  // 필터링 및 페이지네이션
  setFilters: (filters: Partial<ContentFilters>) => void;
  setPagination: (pagination: Partial<PaginationState>) => void;

  // 선택 관리
  setSelectedIds: (ids: number[]) => void;
  toggleSelection: (id: number) => void;
  selectAll: (selectAll: boolean) => void;

  // 상태 관리
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState: SuperChatContentState = {
  contents: [],
  currentContent: null,
  loading: false,
  error: null,
  filters: {
    category: "",
    search: "",
    useYn: "",
  },
  pagination: {
    page: 1,
    limit: 10,
    pageSize: 10,
    total: 0,
    size: 10,
  },
  selectedIds: [],
};

export const useSuperChatContentStore = create<
  SuperChatContentState & SuperChatContentActions
>((set, get) => ({
  ...initialState,

  // 컨텐츠 목록 조회
  fetchContents: async (params) => {
    set({ loading: true, error: null });
    try {
      const { filters, pagination } = get();

      // 안전한 pagination 기본값 보장
      const safePagination = {
        page: 1,
        limit: 10,
        pageSize: 10,
        total: 0,
        size: 10,
        ...pagination, // 기존 값이 있으면 덮어쓰기
      };

      // 빈 값인 파라미터는 제외하여 API 요청
      const queryParams: any = {
        page: safePagination.page,
        limit: safePagination.limit,
        ...params,
      };

      // 빈 값이 아닌 경우에만 파라미터 추가
      if (filters.category && filters.category.trim() !== "") {
        queryParams.category = filters.category;
      }
      if (filters.search && filters.search.trim() !== "") {
        queryParams.search = filters.search;
      }
      if (filters.useYn && filters.useYn.trim() !== "") {
        queryParams.useYn = filters.useYn;
      }

      console.log("[STORE] API 호출 전 파라미터:", {
        filters,
        queryParams,
        useYnValue: filters.useYn,
        useYnIncluded: !!(filters.useYn && filters.useYn.trim() !== ""),
      });

      const result = await superChatContentApi.getList(queryParams);

      // 결과에 따른 안전한 pagination 업데이트
      const updatedPagination = {
        ...safePagination,
        total: Math.max(0, result.count || 0),
        size: safePagination.pageSize, // size와 pageSize 동기화
      };

      set({
        contents: Array.isArray(result.list) ? result.list : [],
        pagination: updatedPagination,
        loading: false,
      });
    } catch (error: any) {
      // 에러 발생 시에도 안전한 상태 유지
      const { pagination } = get();
      const safePagination = {
        page: 1,
        limit: 10,
        pageSize: 10,
        total: 0,
        size: 10,
        ...pagination,
      };

      set({
        contents: [],
        pagination: safePagination,
        error: error.message || "컨텐츠 목록을 불러오는데 실패했습니다.",
        loading: false,
      });
    }
  },

  // 컨텐츠 상세 조회
  fetchContentDetail: async (id) => {
    set({ loading: true, error: null });
    try {
      const content = await superChatContentApi.getDetail(id);
      set({ currentContent: content, loading: false });
    } catch (error: any) {
      set({
        error: error.message || "컨텐츠 상세 정보를 불러오는데 실패했습니다.",
        loading: false,
      });
    }
  },

  // 컨텐츠 생성
  createContent: async (data) => {
    set({ loading: true, error: null });
    try {
      const newContent = await superChatContentApi.create(data);

      // 목록에 새 컨텐츠 추가
      const { contents } = get();
      set({
        contents: [newContent, ...contents],
        loading: false,
      });

      return newContent;
    } catch (error: any) {
      set({
        error: error.message || "컨텐츠 생성에 실패했습니다.",
        loading: false,
      });
      throw error;
    }
  },

  // 컨텐츠 수정
  updateContent: async (id, data) => {
    set({ loading: true, error: null });
    try {
      const updatedContent = await superChatContentApi.update(id, data);

      // 목록에서 해당 컨텐츠 업데이트
      const { contents } = get();
      const updatedContents = contents.map((content) =>
        content.contentId === id ? updatedContent : content
      );

      set({
        contents: updatedContents,
        currentContent: updatedContent,
        loading: false,
      });

      return updatedContent;
    } catch (error: any) {
      set({
        error: error.message || "컨텐츠 수정에 실패했습니다.",
        loading: false,
      });
      throw error;
    }
  },

  // 컨텐츠 삭제
  deleteContent: async (id) => {
    set({ loading: true, error: null });
    try {
      await superChatContentApi.delete(id);

      // 목록에서 해당 컨텐츠 제거
      const { contents } = get();
      const filteredContents = contents.filter(
        (content) => content.contentId !== id
      );

      set({
        contents: filteredContents,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || "컨텐츠 삭제에 실패했습니다.",
        loading: false,
      });
      throw error;
    }
  },

  // 벌크 업데이트
  bulkUpdateContents: async (action, ids) => {
    set({ loading: true, error: null });
    try {
      await superChatContentApi.bulkUpdate(action, ids);

      // 목록 새로고침
      await get().fetchContents();

      // 선택 해제
      set({ selectedIds: [], loading: false });
    } catch (error: any) {
      set({
        error: error.message || "일괄 작업에 실패했습니다.",
        loading: false,
      });
      throw error;
    }
  },

  // 필터 설정
  setFilters: (newFilters) => {
    const { filters } = get();
    set({
      filters: { ...filters, ...newFilters },
      pagination: { ...get().pagination, page: 1 }, // 필터 변경시 첫 페이지로
    });
  },

  // 페이지네이션 설정
  setPagination: (newPagination) => {
    const { pagination } = get();

    // 안전한 기본값으로 현재 pagination 보장
    const safePagination = {
      page: 1,
      limit: 10,
      pageSize: 10,
      total: 0,
      size: 10,
      ...pagination, // 기존 값 유지
    };

    // 새로운 pagination 값 적용
    const updatedPagination = {
      ...safePagination,
      ...newPagination,
    };

    // size와 pageSize 동기화
    if (newPagination.pageSize && !newPagination.size) {
      updatedPagination.size = newPagination.pageSize;
    }
    if (newPagination.size && !newPagination.pageSize) {
      updatedPagination.pageSize = newPagination.size;
    }

    // 유효성 검증
    updatedPagination.page = Math.max(1, updatedPagination.page);
    updatedPagination.limit = Math.max(1, updatedPagination.limit);
    updatedPagination.pageSize = Math.max(1, updatedPagination.pageSize);
    updatedPagination.size = Math.max(1, updatedPagination.size);
    updatedPagination.total = Math.max(0, updatedPagination.total);

    set({ pagination: updatedPagination });
  },

  // 선택된 ID 설정
  setSelectedIds: (ids) => {
    set({ selectedIds: ids });
  },

  // 개별 선택 토글
  toggleSelection: (id) => {
    const { selectedIds } = get();
    const newSelectedIds = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];
    set({ selectedIds: newSelectedIds });
  },

  // 전체 선택/해제
  selectAll: (selectAll) => {
    if (selectAll) {
      const { contents } = get();
      const allIds = contents.map((content) => content.contentId);
      set({ selectedIds: allIds });
    } else {
      set({ selectedIds: [] });
    }
  },

  // 로딩 상태 설정
  setLoading: (loading) => {
    set({ loading });
  },

  // 에러 설정
  setError: (error) => {
    set({ error });
  },

  // 에러 초기화
  clearError: () => {
    set({ error: null });
  },

  // 상태 초기화
  reset: () => {
    set(initialState);
  },
}));
